<template>
  <div class="national-page-wrapper">
    <WeCircularNavbar />

    <section class="national-page" v-if="currentCountry">
      <div class="national-container">
        <button class="back-button" type="button" @click="goBackToCountry">
          ← Back
        </button>

        <h1 class="page-title">
          {{ currentCountry }} - National
          <br />
          strategies
        </h1>

        <p class="page-intro">
          This section provides an overview of key national policy tools and incentive mechanisms supporting women-led
          enterprises, the circular economy, Industry 4.0 adoption, and smart specialisation objectives.
        </p>

        <section class="strategies-panel">
          <div class="strategies-grid">
            <!-- Left column -->
            <div class="strategy-column">
              <h2 class="column-title">Country specific analysis</h2>

              <div v-if="countrySpecificItems.length" class="strategy-list">
                <article
                  v-for="(item, index) in countrySpecificItems"
                  :key="'country-specific-' + index"
                  class="strategy-item"
                >
                  <a
                    v-if="getItemUrl(item)"
                    :href="formatUrl(getItemUrl(item))"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="strategy-link"
                  >
                    <span class="strategy-text">
                      {{ getItemTitle(item) }}
                    </span>

                    <span class="arrow-circle" aria-hidden="true">
                      →
                    </span>
                  </a>

                  <div v-else class="strategy-link strategy-link--static">
                    <span class="strategy-text">
                      {{ getItemTitle(item) }}
                    </span>

                    <span class="arrow-circle arrow-circle--disabled" aria-hidden="true">
                      →
                    </span>
                  </div>
                </article>
              </div>

              <div v-else class="empty-state">
                No country specific analysis available for this country.
              </div>
            </div>

            <!-- Right column -->
            <div class="strategy-column">
              <h2 class="column-title">Strategic documents</h2>

              <div v-if="strategicDocumentItems.length" class="strategy-list">
                <article
                  v-for="(item, index) in strategicDocumentItems"
                  :key="'strategic-document-' + index"
                  class="strategy-item"
                >
                  <a
                    v-if="getItemUrl(item)"
                    :href="formatUrl(getItemUrl(item))"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="strategy-link"
                  >
                    <span class="strategy-text">
                      {{ getItemTitle(item) }}
                    </span>

                    <span class="arrow-circle" aria-hidden="true">
                      →
                    </span>
                  </a>

                  <div v-else class="strategy-link strategy-link--static">
                    <span class="strategy-text">
                      {{ getItemTitle(item) }}
                    </span>

                    <span class="arrow-circle arrow-circle--disabled" aria-hidden="true">
                      →
                    </span>
                  </div>
                </article>
              </div>

              <div v-else class="empty-state">
                No strategic documents available for this country.
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'

import { countrySpecificAnalysisData } from './national_strategies_country_specific_analysis.js'
import { strategicDocumentsData } from './national_strategies_strategic_documents.js'

export default {
  name: 'NationalStrategies',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },

  data() {
    return {
      selectedCountry: this.getCountryFromRoute()
    }
  },

  computed: {
    currentCountry() {
      return this.selectedCountry
    },

    countrySpecificItems() {
      return this.getCountryItems(countrySpecificAnalysisData, this.currentCountry)
    },

    strategicDocumentItems() {
      return this.getCountryItems(strategicDocumentsData, this.currentCountry)
    }
  },

  watch: {
    '$route.query.country'() {
      this.selectedCountry = this.getCountryFromRoute()
    },
    '$route.params.country'() {
      this.selectedCountry = this.getCountryFromRoute()
    }
  },

  methods: {
    goBackToCountry() {
      if (this.$route.query.country) {
        this.$router.push({
          path: '/we-circular',
          query: {
            country: this.$route.query.country
          },
          hash: '#goals'
        })
      } else {
        this.$router.push('/we-circular')
      }
    },
    getCountryFromRoute() {
      const country = this.$route.query.country || this.$route.params.country || 'Hungary'

      if (typeof country !== 'string') {
        return 'Hungary'
      }

      return country
    },

    cleanText(value) {
      return (value || '').toString().trim()
    },

    normalizeText(value) {
      return this.cleanText(value)
        .toLowerCase()
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
    },

    getCountryItems(source, country) {
      if (!source || !country) {
        return []
      }

      const normalizedCountry = this.normalizeText(country)

      const matchedKey = Object.keys(source).find(key => {
        const normalizedKey = this.normalizeText(key)

        return (
          normalizedKey === normalizedCountry ||
          normalizedKey.includes(normalizedCountry) ||
          normalizedCountry.includes(normalizedKey)
        )
      })

      return matchedKey ? source[matchedKey] : []
    },

    getItemTitle(item) {
      const englishTitle = this.cleanText(item?.englishTitle)
      const localTitle = this.cleanText(item?.localTitle)
      const programmeName = this.cleanText(item?.['Name of the programme'])

      if (englishTitle && localTitle && englishTitle !== localTitle) {
        return `${englishTitle} - ${localTitle}`
      }

      return englishTitle || localTitle || programmeName || '-'
    },

    getItemUrl(item) {
      return item?.link || item?.Website || ''
    },
    formatUrl(url) {
      const trimmed = this.cleanText(url)

      if (!trimmed) {
        return '#'
      }

      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed
      }

      return `https://${trimmed}`
    }
  }
}
</script>

<style scoped>
.national-page {
  min-height: 100vh;
  background: #f3f3f3;
  padding: 22px 0 64px;
}

.national-container {
  width: 100%;
  max-width: 80%;
  margin: 0 auto;
  padding: 0 38px;
  box-sizing: border-box;
}

.back-button {
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  height: 30px;
  min-width: 66px;
  padding: 0 12px;
  margin-bottom: 22px;
  border: 1px solid #003399;
  border-radius: 8px;
  background: transparent;
  color: #003399;
  line-height: 1;
  cursor: pointer;
  margin-left: 0;
}

.page-title {
  margin: 0;
  max-width: 580px;
  color: #003399;
  line-height: 1.02;
  font-weight: 600;
  letter-spacing: -0.03em;
  text-align: left;
}

.page-intro {
  max-width: 320px;
  margin: 16px 0 26px;
  color: #0e6eb6;
  line-height: 1.55;
  text-align: left;
}

.strategies-panel {
  position: relative;
  width: 100%;
  min-height: 405px;
  border-radius: 14px;
  overflow: hidden;
  background: #004f4f;
  background-image: url('../../assets/img/we-circular/9-16@4x.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 22px 28px 26px;
  box-sizing: border-box;
}

.strategies-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 79, 79, 0.42);
  pointer-events: none;
}

.strategies-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 44px;
}

.strategy-column {
  min-width: 0;
}

.column-title {
  margin: 0 0 18px;
  color: #ffffff;
  font-weight: 600;
  line-height: 1.25;
  text-align: left;
}

.strategy-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.strategy-item {
  width: 100%;
}

.strategy-link {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  text-decoration: none;
  color: #ffffff;
}

.strategy-link:hover .strategy-text {
  text-decoration: underline;
}

.strategy-link--static {
  cursor: default;
}

.strategy-text {
  display: inline-block;
  flex: 1;
  color: #ffffff;
  line-height: 1.45;
  text-align: left;
  word-break: break-word;
}

.arrow-circle {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #005ed9;
  color: #ffffff;
  line-height: 1;
  margin-top: 2px;
}

.arrow-circle--disabled {
  opacity: 0.4;
}

.empty-state {
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  line-height: 1.45;
  text-align: left;
}

@media (max-width: 1100px) {
  .national-container {
    max-width: 92%;
    padding: 0 20px;
  }

  .strategies-grid {
    gap: 28px;
  }
}

@media (max-width: 900px) {
  .page-title {
    font-size: 28px;
  }

  .column-title {
    font-size: 20px;
  }

  .strategies-grid {
    grid-template-columns: 1fr;
  }

  .strategies-panel {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .national-page {
    padding-bottom: 48px;
  }

  .page-title {
    font-size: 22px;
  }

  .page-intro {
    max-width: 100%;
    font-size: 12px;
  }

  .column-title {
    font-size: 18px;
  }

  .strategy-text {
    font-size: 12px;
  }

  .arrow-circle {
    width: 16px;
    height: 16px;
    font-size: 11px;
  }
}
</style>
